import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, TextField, Button } from '@mui/material';
import { CheckCircle, Cancel, Info } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import Table from '../../../components/table/Table';
import {
  useUpdateMakerCheckerMutation,
  useGetMakerCheckerStatusLookupQuery,
  useGetIbanByIdQuery,
  useSendMoneyMutation,
  useWithdrawalMutation,
  useSendMoneyPhoneNumberListMutation,
  useSendMealAllowanceListMutation,
  useSendMealAllowanceListWithWalletNoMutation,
  useSendCashbackListMutation,
  useSendMoneyListMutation,
} from '../services/approvalService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  MakerCheckerItem,
  FastOutgoingMessagesDto,
  SendMoneyListDto,
  WithdrawalDto,
  Iban,
} from '../../../types/approval.types';
import { typeToDescription } from '../../../utils/approvalUtils';
import FastTransactionDetail from '../components/FastTransactionDetail';
import WithdrawalTransactionDetail from '../components/WithdrawalTransactionDetail';
import SendMoneyTransactionDetail from '../components/SendMoneyTransactionDetail';
import BulkSendMoneyTransactionDetail from '../components/BulkSendMoneyTransactionDetail';
import SendMoneyCancelDetail from '../components/SendMoneyCancelDetail';
import WithdrawalCancelDetail from '../components/WithdrawalCancelDetail';
import FastCancelDetail from '../components/FastCancelDetail';
import { useFetchDataQuery, useProcessTypeLookupQuery } from '../../../components/services/services';
import { filterOperatorsDateTime } from '../../../utils/filter-date-time';
import { useFastoutgoingmessagesMutation } from '../../fast/services/services';
import { usePermission } from '../../../hooks/usePermission';

interface MakerCheckerListProps {
  onRefresh?: () => void;
}

const MakerCheckerList: React.FC<MakerCheckerListProps> = ({ onRefresh }) => {
  const { hasPermission: canViewApprovalList, isLoading: isPermissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.Onay/RedListesineErişim',
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Modal states
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MakerCheckerItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Transaction data states
  const [fastData, setFastData] = useState<FastOutgoingMessagesDto | null>(null);
  const [ibanData, setIbanData] = useState<Iban | null>(null);
  const [sendMoneyData, setSendMoneyData] = useState<SendMoneyListDto | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalDto | null>(null);

  const [bulkSendMoneyListWithWalletNoDto, setBulkSendMoneyListWithWalletNoDto] = useState<any>(null);
  const [bulkSendMoneyListWithPhoneNoDto, setBulkSendMoneyListWithPhoneNoDto] = useState<any>(null);
  const [bulkSendMealAllowanceListWithWalletNoDto, setBulkSendMealAllowanceListWithWalletNoDto] = useState<any>(null);
  const [bulkSendMealAllowanceListWithCardNoDto, setBulkSendMealAllowanceListWithCardNoDto] = useState<any>(null);
  const [bulkSendCashbackListDto, setBulkSendCashbackListDto] = useState<any>(null);

  const currentUserState = useSelector((state: RootState) => state.currentUserState.currentUserState);
  const listUrl = `/MakerChecker/GetMakerCheckersListV2`;
  const [requestBody, setRequestBody] = useState({
    filters: [
      {
        field: "status",
        values: ["0"],
        valueType: 1,
        operationType: 1,
        logicOperator: "string"
      }
    ],
    pageSize: 25,
    page: 1,
    orderBy: '',
    orderDesc: false,
    companyId: currentUserState?.companyId || undefined,
  });
  const {
    data: fetchData,
    isFetching,
    refetch,
  } = useFetchDataQuery({ url: listUrl, filter: requestBody, method: 'POST' }, {
    skip: !requestBody?.companyId || !canViewApprovalList || isPermissionLoading,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });

  // Kurum değişince requestBody.companyId güncellenmezse liste refetch olmayabilir
  useEffect(() => {
    if (!currentUserState?.companyId) return;
    setPaginationModel({ page: 0, pageSize: 25 });
    setRequestBody((prev) => ({
      ...prev,
      pageSize: 25,
      page: 1,
      orderBy: '',
      orderDesc: false,
      companyId: currentUserState.companyId,
    }));
  }, [currentUserState?.companyId]);

  // Component mount olduğunda veya companyId değiştiğinde refetch yap
  useEffect(() => {
    if (requestBody?.companyId && canViewApprovalList && !isPermissionLoading) {
      refetch();
    }
  }, [requestBody?.companyId, refetch, canViewApprovalList, isPermissionLoading]);

  // Tab'e geri dönüldüğünde refetch yap
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && requestBody?.companyId && canViewApprovalList && !isPermissionLoading) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestBody?.companyId, refetch, canViewApprovalList, isPermissionLoading]);

  // API queries and mutations
  const { data: statusTypes } = useGetMakerCheckerStatusLookupQuery(undefined, {
    skip: !canViewApprovalList || isPermissionLoading,
  });
  const { data: processEnumData } = useProcessTypeLookupQuery(undefined, {
    skip: !canViewApprovalList || isPermissionLoading,
  });

  const [updateMakerChecker, { isLoading: isLoadingUpdate }] = useUpdateMakerCheckerMutation();
  const [sendMoneyMutation, { isLoading: isLoadingSendMoney }] = useSendMoneyMutation();
  const [withdrawalMutation, { isLoading: isLoadingWithDrawal }] = useWithdrawalMutation();
  const [fastOutgoingMessages, { isLoading: isTransferLoading }] = useFastoutgoingmessagesMutation();
  const [sendMoneyList] = useSendMoneyListMutation();
  const [sendMoneyPhoneNumberList] = useSendMoneyPhoneNumberListMutation();
  const [sendMealAllowanceList] = useSendMealAllowanceListMutation();
  const [sendMealAllowanceListWithWalletNo] = useSendMealAllowanceListWithWalletNoMutation();
  const [sendCashbackListDtoAsync] = useSendCashbackListMutation();

  // Get IBAN data when needed
  const { data: ibanResponse, refetch: refetchGetIban } = useGetIbanByIdQuery(withdrawalData?.Data?.IbanId || 0, {
    skip: !withdrawalData?.Data?.IbanId,
  });

  React.useEffect(() => {
    if (ibanResponse?.data) {
      setIbanData(ibanResponse.data);
    }
  }, [ibanResponse]);

  const createModelForType = (json: string) => {
    try {
      return JSON.parse(json);
    } catch (error) {
      return null;
    }
  };

  const createModel = useCallback((json: string, type: string) => {
    try {
      const parsedData = createModelForType(json);
      
      // SendMoneyListDto için Data kontrolü
      if (type === 'SendMoneyListDto') {
        if (!parsedData || !parsedData.Data || !Array.isArray(parsedData.Data) || parsedData.Data.length === 0) {
          enqueueSnackbar('Geçersiz veri formatı: Data bulunamadı veya boş.', { variant: 'error' });
          return false;
        }
      }
      
      switch (type) {
        case 'WithdrawalDto':
          setWithdrawalData(parsedData);
          refetchGetIban();
          break;
        case 'SendMoneyListDto':
          setSendMoneyData(parsedData);
          break;
        case 'FastOutgoingMessagesDto':
          setFastData(parsedData);
          break;
        case 'BulkSendMoneyListWithWalletNoDto':
          setBulkSendMoneyListWithWalletNoDto(parsedData);
          break;
        case 'BulkSendMoneyListWithPhoneNoDto':
          setBulkSendMoneyListWithPhoneNoDto(parsedData);
          break;
        case 'BulkSendMealAllowanceListWithWalletNoDto':
          setBulkSendMealAllowanceListWithWalletNoDto(parsedData);
          break;
        case 'BulkSendMealAllowanceListWithCardNoDto':
          setBulkSendMealAllowanceListWithCardNoDto(parsedData);
          break;
        case 'BulkSendCashbackListDto':
          setBulkSendCashbackListDto(parsedData);
          break;
        default:
          break;
      }
      return true;
    } catch (error) {
      return false;
    }
  }, [refetchGetIban]);

  const handleApproval = useCallback(
    (item: MakerCheckerItem) => {
      setSelectedItem(item);
      createModel(item.jsonNewValue, item.reflectionType);
      setIsApprovalModalOpen(true);
    },
    [createModel]
  );

  const handleCancel = useCallback(
    (item: MakerCheckerItem) => {
      setSelectedItem(item);
      createModel(item.jsonNewValue, item.reflectionType);
      setIsCancelModalOpen(true);
    },
    [createModel]
  );

  const handleDetail = useCallback(
    (item: MakerCheckerItem) => {
      setSelectedItem(item);
      createModel(item.jsonNewValue, item.reflectionType);
      setIsDetailModalOpen(true);
    },
    [createModel]
  );

  const confirmApproval = useCallback(async () => {
    if (!selectedItem) return;
    try {
      if (selectedItem.reflectionType === 'FastOutgoingMessagesDto' && fastData) {
        // Fast transaction
        const fastRequest = {
          currencyId: fastData.CurrencyId,
          value: fastData.Value,
          description: fastData.Description,
          valueFee: fastData.ValueFee,
          receiverIBAN: fastData.ReceiverIBAN,
          receiverNameSurname: fastData.ReceiverNameSurname,
          receiverIdNo: fastData.ReceiverIdNo,
          paymentPurpose: fastData.PaymentPurpose,
          companyId: fastData.CompanyId,
          customerId: fastData.CustomerId,
          isChecker: true,
        };

        const result = await fastOutgoingMessages(fastRequest);
        if (result.data.success) {
          try {
            let response = await updateMakerChecker({
              id: selectedItem.id,
              status: 1,
              rejectionReason: '',
            }).unwrap();
            enqueueSnackbar(response?.message, { variant: 'success' });
          } catch (error) {
            console.log(error);
          }
        } else {
          console.log(result?.data?.message);
        }
      } else if (selectedItem.reflectionType === 'SendMoneyListDto' && sendMoneyData) {
        // Send money transaction
        // Data kontrolü - eğer Data yoksa veya array değilse hata göster
        if (!sendMoneyData.Data || !Array.isArray(sendMoneyData.Data) || sendMoneyData.Data.length === 0) {
          enqueueSnackbar('Geçersiz veri formatı: Data bulunamadı veya boş.', { variant: 'error' });
          setIsApprovalModalOpen(false);
          return;
        }

        const sendMoneyRequest = {
          data: sendMoneyData.Data.map((item) => ({
            currencyId: item.CurrencyId.toString(),
            value: item.Value.toString(),
            receiverWalletNo: item.ReceiverWalletNo,
            description: item.Description,
          })),
          companyId: sendMoneyData.CompanyId,
          customerId: sendMoneyData.CustomerId,
          isChecker: true,
        };

        const result = await sendMoneyMutation(sendMoneyRequest).unwrap();

        if (result?.data[0]?.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar('İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result?.data[0].message, { variant: 'error' });
        }
      } else if (selectedItem.reflectionType === 'WithdrawalDto' && withdrawalData) {
        // Withdrawal transaction
        const withdrawalRequest = {
          data: {
            currencyId: withdrawalData.Data.CurrencyId,
            value: withdrawalData.Data.Value,
            description: withdrawalData.Data.Description,
            valueFee: withdrawalData.Data.ValueFee,
            ibanId: withdrawalData.Data.IbanId,
          },
          userId: withdrawalData.UserId,
          companyId: withdrawalData.CompanyId,
          customerId: withdrawalData.CustomerId,
          isChecker: true,
        };

        const result = await withdrawalMutation(withdrawalRequest).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar('İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      } else if (
        selectedItem.reflectionType === 'BulkSendMoneyListWithWalletNoDto' &&
        bulkSendMoneyListWithWalletNoDto
      ) {
        // Bulk send money with wallet number
        const bulkSendMoneyListWithWalletNoArray = bulkSendMoneyListWithWalletNoDto.Data.map((item: any) => ({
          description: item.Description,
          receiverWalletNo: item.ReceiverWalletNo,
          currencyId: item.CurrencyId,
          value: item.Value,
        }));

        const bulkSendMoneyListWithWalletNoApi = {
          data: bulkSendMoneyListWithWalletNoArray,
          companyId: bulkSendMoneyListWithWalletNoDto.CompanyId,
          customerId: bulkSendMoneyListWithWalletNoDto.CustomerId,
        };

        const result = await sendMoneyList(bulkSendMoneyListWithWalletNoApi).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar(result.message || 'İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      } else if (selectedItem.reflectionType === 'BulkSendMoneyListWithPhoneNoDto' && bulkSendMoneyListWithPhoneNoDto) {
        // Bulk send money with phone number
        const bulkSendMoneyListWithPhoneNoArray = bulkSendMoneyListWithPhoneNoDto.Data.map((item: any) => ({
          description: item.Description,
          receiverPhoneNumber: item.ReceiverPhoneNumber,
          currencyId: Number(item.CurrencyId),
          value: Number(item.Value),
        }));

        const bulkSendMoneyListWithPhoneNoApi = {
          data: bulkSendMoneyListWithPhoneNoArray,
          companyId: bulkSendMoneyListWithPhoneNoDto.CompanyId,
          customerId: bulkSendMoneyListWithPhoneNoDto.CustomerId,
        };

        const result = await sendMoneyPhoneNumberList(bulkSendMoneyListWithPhoneNoApi).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar(result.message || 'İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      } else if (
        selectedItem.reflectionType === 'BulkSendMealAllowanceListWithCardNoDto' &&
        bulkSendMealAllowanceListWithCardNoDto
      ) {
        // Bulk send meal allowance with card number
        const sendMealAllowanceListArray = bulkSendMealAllowanceListWithCardNoDto.Data.map((item: any) => ({
          description: item.Description,
          receiverCardNo: item.ReceiverCardNo,
          currencyId: item.CurrencyId,
          value: Number(item.Value),
        }));

        const bulkSendMealAllowanceListApi = {
          data: sendMealAllowanceListArray,
          companyId: bulkSendMealAllowanceListWithCardNoDto.CompanyId,
          customerId: bulkSendMealAllowanceListWithCardNoDto.CustomerId,
          scheduledAt: bulkSendMealAllowanceListWithCardNoDto.ScheduledAt,
        };

        const result = await sendMealAllowanceList(bulkSendMealAllowanceListApi).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar(result.message || 'İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      } else if (
        selectedItem.reflectionType === 'BulkSendMealAllowanceListWithWalletNoDto' &&
        bulkSendMealAllowanceListWithWalletNoDto
      ) {
        // Bulk send meal allowance with wallet number
        const sendMealAllowanceListArray = bulkSendMealAllowanceListWithWalletNoDto.Data.map((item: any) => ({
          description: item.Description,
          receiverWalletNo: item.ReceiverWalletNo,
          currencyId: item.CurrencyId,
          value: item.Value,
        }));

        const bulkSendMealAllowanceListApi = {
          data: sendMealAllowanceListArray,
          companyId: bulkSendMealAllowanceListWithWalletNoDto.CompanyId,
          customerId: bulkSendMealAllowanceListWithWalletNoDto.CustomerId,
          scheduledAt: bulkSendMealAllowanceListWithWalletNoDto.ScheduledAt,
        };

        const result = await sendMealAllowanceListWithWalletNo(bulkSendMealAllowanceListApi).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar(result.message || 'İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      } else if (selectedItem.reflectionType === 'BulkSendCashbackListDto' && bulkSendCashbackListDto) {
        // Bulk send cashback
        const result = await sendCashbackListDtoAsync(bulkSendCashbackListDto).unwrap();

        if (result.success) {
          await updateMakerChecker({
            id: selectedItem.id,
            status: 1,
            rejectionReason: '',
          }).unwrap();

          enqueueSnackbar(result.message || 'İşlem başarıyla onaylandı', { variant: 'success' });
        } else {
          enqueueSnackbar(result.message, { variant: 'error' });
        }
      }

      setIsApprovalModalOpen(false);
      refetch();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.log(error);
    }
  }, [
    selectedItem,
    fastData,
    sendMoneyData,
    withdrawalData,
    bulkSendMoneyListWithWalletNoDto,
    bulkSendMoneyListWithPhoneNoDto,
    bulkSendMealAllowanceListWithCardNoDto,
    bulkSendMealAllowanceListWithWalletNoDto,
    bulkSendCashbackListDto,
    refetch,
    onRefresh,
    fastOutgoingMessages,
    updateMakerChecker,
    sendMoneyMutation,
    withdrawalMutation,
    sendMoneyList,
    sendMoneyPhoneNumberList,
    sendMealAllowanceList,
    sendMealAllowanceListWithWalletNo,
    sendCashbackListDtoAsync,]);

  const confirmCancel = useCallback(async () => {
    if (!selectedItem || !rejectionReason.trim()) return;
    try {
      await updateMakerChecker({
        id: selectedItem.id,
        status: 2,
        rejectionReason: rejectionReason,
      }).unwrap();

      enqueueSnackbar('İşlem başarıyla iptal edildi', { variant: 'success' });
      setIsCancelModalOpen(false);
      setRejectionReason('');
      refetch();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.log(error);
    }
  }, [selectedItem, rejectionReason, updateMakerChecker, refetch, onRefresh]);
  const initialColumns: any = useMemo(
    () => [
      {
        field: 'createDate',
        headerName: 'İşlem Tarihi',
        width: 180,
        type: 'datetime',
        filterOperators: filterOperatorsDateTime,
      },
      {
        field: 'type',
        headerName: 'İşlem Türü',
        type: 'singleSelect',
        valueOptions: [],
        width: 200,
      },
      {
        field: 'status',
        type: 'singleSelect',
        valueOptions: [],
        headerName: 'Durumu',
        width: 120,
      },
      {
        field: 'makerName',
        headerName: 'İşlemi Yapan Kullanıcı',
        width: 200,
      },
      {
        field: 'actions',
        headerName: 'İşlemler',
        width:300,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const item = params.row as MakerCheckerItem;

          const status = typeof item.status === 'string' ? parseInt(item.status) : item.status;
          const isApproved = status === 1;
          const isCancelled = status === 2;

          const isDisabled = isApproved || isCancelled;

          return<Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1, flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApproval(item)}
                    disabled={isDisabled}
                    sx={{
                      bgcolor: '#4caf50',
                      minWidth: '80px',
                      fontSize: '11px',
                      py: 0.5,
                      '&:hover': { bgcolor: '#45a049' },
                    }}
                  >
                    {
                      item.reflectionType === 'BulkSendMoneyListWithWalletNoDto' ||
                        item.reflectionType === 'BulkSendMoneyListWithPhoneNoDto' ||
                        item.reflectionType === 'BulkSendMealAllowanceListWithWalletNoDto' ||
                        item.reflectionType === 'BulkSendMealAllowanceListWithCardNoDto' ||
                        item.reflectionType === 'BulkSendCashbackListDto'
                        ? 'Toplu İşlem Onayı'
                        : 'Onay'

                    }
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Cancel />}
                    onClick={() => handleCancel(item)}
                    disabled={isDisabled}
                    sx={{
                      bgcolor: '#f44336',
                      minWidth: '70px',
                      fontSize: '11px',
                      py: 0.5,
                      '&:hover': { bgcolor: '#da190b' },
                    }}
                  >
                    {
                      item.reflectionType === 'BulkSendMoneyListWithWalletNoDto' ||
                        item.reflectionType === 'BulkSendMoneyListWithPhoneNoDto' ||
                        item.reflectionType === 'BulkSendMealAllowanceListWithWalletNoDto' ||
                        item.reflectionType === 'BulkSendMealAllowanceListWithCardNoDto' ||
                        item.reflectionType === 'BulkSendCashbackListDto'
                        ? 'Toplu İşlem İptali'
                        : 'İptal'

                    }

                  </Button>
                </Box>
              </Box>
        },
      },
    ],
    [handleApproval, handleCancel]
  );
  const [columns, setColumns] = useState<GridColDef[]>(initialColumns);

  const renderTransactionDetail = () => {
    if (!selectedItem) return null;
    switch (selectedItem.reflectionType) {
      case 'FastOutgoingMessagesDto':
        return fastData ? <FastTransactionDetail data={fastData} /> : null;
      case 'WithdrawalDto':
        return withdrawalData && ibanData ? (
          <WithdrawalTransactionDetail data={withdrawalData} iban={ibanData} />
        ) : null;
      case 'SendMoneyListDto':
        if (!sendMoneyData || !sendMoneyData.Data || !Array.isArray(sendMoneyData.Data) || sendMoneyData.Data.length === 0) {
          return null;
        }
        return sendMoneyData ? <SendMoneyTransactionDetail data={sendMoneyData} /> : null;
      case 'BulkSendMoneyListWithWalletNoDto':
        return bulkSendMoneyListWithWalletNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMoneyListWithWalletNoDto} />
        ) : null;
      case 'BulkSendMoneyListWithPhoneNoDto':
        return bulkSendMoneyListWithPhoneNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMoneyListWithPhoneNoDto} />
        ) : null;
      case 'BulkSendMealAllowanceListWithWalletNoDto':
        return bulkSendMealAllowanceListWithWalletNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMealAllowanceListWithWalletNoDto} />
        ) : null;
      case 'BulkSendMealAllowanceListWithCardNoDto':
        return bulkSendMealAllowanceListWithCardNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMealAllowanceListWithCardNoDto} />
        ) : null;
      case 'BulkSendCashbackListDto':
        return bulkSendCashbackListDto ? <BulkSendMoneyTransactionDetail data={bulkSendCashbackListDto} /> : null;

      default:
        return null;
    }
  };

  const renderCancelDetail = () => {
    if (!selectedItem) return null;

    switch (selectedItem.reflectionType) {
      case 'SendMoneyListDto':
        if (!sendMoneyData || !sendMoneyData.Data || !Array.isArray(sendMoneyData.Data) || sendMoneyData.Data.length === 0) {
          return null;
        }
        return sendMoneyData ? (
          <SendMoneyCancelDetail
            data={sendMoneyData}
            rejectionReason={rejectionReason}
            onReasonChange={setRejectionReason}
            onSubmit={confirmCancel}
            isLoading={false}
          />
        ) : null;
      case 'WithdrawalDto':
        return withdrawalData && ibanData ? (
          <WithdrawalCancelDetail
            data={withdrawalData}
            iban={ibanData}
            rejectionReason={rejectionReason}
            onReasonChange={setRejectionReason}
            onSubmit={confirmCancel}
            isLoading={false}
          />
        ) : null;
      case 'FastOutgoingMessagesDto':
        return fastData ? (
          <FastCancelDetail
            data={fastData}
            rejectionReason={rejectionReason}
            onReasonChange={setRejectionReason}
            onSubmit={confirmCancel}
            isLoading={false}
          />
        ) : null;
      default:
        return (
          <Box sx={{ p: 2 }}>
            {renderTransactionDetail()}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="İptal Nedeni"
                value={rejectionReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRejectionReason(e.target.value)}
                variant="outlined"
                size="small"
                required
                error={!rejectionReason.trim()}
                helperText={!rejectionReason.trim() ? 'Lütfen iptal nedenini giriniz' : ''}
              />
            </Box>
          </Box>
        );
    }
  };
  useEffect(() => {
    const updatedColumns = initialColumns.map((column: any) => {
      if (column.field === 'type' && (processEnumData?.data ?? []).length > 0) {
        return {
          ...column,
          valueOptions: processEnumData?.data.map((item: any) => ({
            value: item.value,
            label: item.text,
          })),
        };
      }
      if (column.field === 'status' && (statusTypes?.data ?? []).length > 0) {
        return {
          ...column,
          valueOptions: statusTypes?.data?.map((item: any) => ({
            value: item.value,
            label: item.text,
          })),
        };
      }
      return column;
    });
    setColumns([...updatedColumns]);
  }, [processEnumData, fetchData?.data, statusTypes, initialColumns]);

  const debounce = (func: any, delay: any) => {
    let timeoutId: any;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };
  const onFilterChange = debounce((filterObj: any) => {
    const statusFilter = {
      field: "status",
      values: ["0"],
      valueType: 1,
      operationType: 1,
      logicOperator: "string"
    };

    const combinedFilters = filterObj ? [statusFilter, ...filterObj] : [statusFilter];

    setRequestBody((prev) => ({ ...prev, filters: combinedFilters }));
  }, 500);

  const handleChangePagination = (model: any) => {
    setPaginationModel(model);
    setRequestBody((prev) => ({
      ...prev,
      pageSize: model.pageSize,
      page: model.page + 1,
    }));
  };

  const sortModelChange = (model: any) => {
    setRequestBody((prev) => ({
      ...prev,
      orderBy: model[0]?.field ?? '',
      orderDesc: model[0]?.sort === 'asc',
    }));
  };

  return (
    <Box>
      <Table
        sx={{
          margin: fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? '0px 0 25px 0 !important' : '0px !important',
        }}
        columnVisibilityConfigKey="makerChecker-list"
        filterKey="makerChecker-filter"
        sortModelChange={sortModelChange}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        checkboxSelection
        isMasterDetailActive={false}
        headerFilters={true}
        rows={fetchData?.data ?? []}
        columns={columns}
        isLoading={isPermissionLoading || isFetching || (fetchData === undefined && !!requestBody?.companyId)}
        handleFilterChange={onFilterChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handleChangePagination}
        pageSizeOptions={[25, 50, 100]}
        rowCount={fetchData?.totalCount ?? 0}
        getRowId={(row: any) => row.id}
        idName={'id'}
        listUrl={listUrl}
        fetchData={fetchData}
        companyId={currentUserState?.companyId || undefined}
        showDownloadButton={true}
        listName="Onay Bekleyen İşlemler Listesi"
        requestBody={requestBody}
        fetchMethod="POST"
      />

      {/* Approval Modal */}
      <ConfirmationModal
        isOpen={isApprovalModalOpen}
        onClose={() => {
          setWithdrawalData(null);
          setSendMoneyData(null);
          setIsApprovalModalOpen(false);
          setRejectionReason('');
          setSelectedItem(null);
        }}
        onConfirm={confirmApproval}
        title={selectedItem ? `${typeToDescription(selectedItem)} Onay İşlemi` : 'Onay İşlemi'}
        confirmButtonText="Onayla"
        closeButtonText="İptal"
        isLoading={isTransferLoading}
        optionalWidth="800px"
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
          titleColor: 'white',
          icon: <CheckCircle sx={{ color: 'white' }} />,
          buttonColor: '#4caf50',
        }}
      >
        {renderTransactionDetail()}
      </ConfirmationModal>

      {/* Cancel Modal */}
      <ConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title={selectedItem ? `${typeToDescription(selectedItem)} İptal İşlemi` : 'İptal İşlemi'}
        onConfirm={confirmCancel}
        confirmButtonText="İptal Et"
        closeButtonText="Vazgeç"
        isLoading={isLoadingUpdate}
        confirmButtonDisabled={!rejectionReason.trim()}
        optionalWidth="800px"
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
          titleColor: 'white',
          icon: <Cancel sx={{ color: 'white' }} />,
          buttonColor: '#f44336',
        }}
      >
        {renderCancelDetail()}
      </ConfirmationModal>

      {/* Detail Modal */}
      <ConfirmationModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={selectedItem ? `${typeToDescription(selectedItem)} İşlem Detayı` : 'İşlem Detayı'}
        confirmButtonText=""
        closeButtonText=""
        isLoading={isLoadingWithDrawal || isLoadingSendMoney || isTransferLoading}
        optionalWidth="800px"
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
          titleColor: 'white',
          icon: <Info sx={{ color: 'white' }} />,
          buttonColor: '#2196f3',
        }}
      >
        {renderTransactionDetail()}
      </ConfirmationModal>
    </Box>
  );
};

export default MakerCheckerList;
